import React, { useState, useEffect } from 'react';
import api from '../api';
import { Plus, Edit2, Key, Users } from 'lucide-react';

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [resetUserId, setResetUserId] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [form, setForm] = useState({
    username: '', password: '', first_name: '', last_name: '',
    email: '', role: 'user', weekly_hours: 40,
  });

  const fetchUsers = async () => {
    try {
      const res = await api.get('/users');
      setUsers(res.data);
    } catch (error) {
      console.error('Fehler:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const openCreateModal = () => {
    setEditUser(null);
    setForm({ username: '', password: '', first_name: '', last_name: '', email: '', role: 'user', weekly_hours: 40 });
    setShowModal(true);
  };

  const openEditModal = (user) => {
    setEditUser(user);
    setForm({
      first_name: user.first_name, last_name: user.last_name,
      email: user.email || '', role: user.role, weekly_hours: user.weekly_hours,
      is_active: user.is_active,
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editUser) {
        const { username, password, ...updateData } = form;
        await api.put(`/users/${editUser.id}`, updateData);
      } else {
        await api.post('/users', form);
      }
      setShowModal(false);
      fetchUsers();
    } catch (error) {
      alert(error.response?.data?.details?.join('\n') || error.response?.data?.error || 'Fehler beim Speichern.');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/users/${resetUserId}/reset-password`, { new_password: newPassword });
      setShowResetModal(false);
      setNewPassword('');
      alert('Passwort erfolgreich zurückgesetzt.');
    } catch (error) {
      alert(error.response?.data?.error || 'Fehler beim Zurücksetzen.');
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Laden...</div>;

  return (
    <div>
      <div className="page-header">
        <div className="page-header-actions">
          <div>
            <h2>Benutzerverwaltung</h2>
            <p>{users.length} Benutzer registriert</p>
          </div>
          <button className="btn btn-primary" onClick={openCreateModal}>
            <Plus size={16} /> Neuer Benutzer
          </button>
        </div>
      </div>

      <div className="card">
        <div className="table-container">
          {users.length > 0 ? (
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Benutzername</th>
                  <th>E-Mail</th>
                  <th>Rolle</th>
                  <th>Wochenstunden</th>
                  <th>Status</th>
                  <th>Erstellt am</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id}>
                    <td><strong>{u.first_name} {u.last_name}</strong></td>
                    <td>{u.username}</td>
                    <td>{u.email || '-'}</td>
                    <td><span className={`badge badge-${u.role}`}>{u.role === 'admin' ? 'Admin' : 'Benutzer'}</span></td>
                    <td>{u.weekly_hours}h</td>
                    <td>
                      <span className={`badge badge-${u.is_active ? 'active' : 'inactive'}`}>
                        {u.is_active ? 'Aktiv' : 'Inaktiv'}
                      </span>
                    </td>
                    <td>{new Date(u.created_at).toLocaleDateString('de-DE')}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className="btn btn-secondary btn-sm btn-icon" onClick={() => openEditModal(u)} title="Bearbeiten">
                          <Edit2 size={14} />
                        </button>
                        <button className="btn btn-secondary btn-sm btn-icon" title="Passwort zurücksetzen"
                          onClick={() => { setResetUserId(u.id); setShowResetModal(true); }}>
                          <Key size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="empty-state">
              <Users size={40} />
              <p>Keine Benutzer vorhanden.</p>
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editUser ? 'Benutzer bearbeiten' : 'Neuer Benutzer'}</h3>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowModal(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {!editUser && (
                  <>
                    <div className="form-group">
                      <label>Benutzername</label>
                      <input type="text" className="form-control" value={form.username}
                        onChange={(e) => setForm({ ...form, username: e.target.value })} required minLength={3} />
                    </div>
                    <div className="form-group">
                      <label>Passwort</label>
                      <input type="password" className="form-control" value={form.password}
                        onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={10} />
                      <span className="form-hint">Min. 10 Zeichen, Groß-/Kleinbuchstaben und Zahl</span>
                    </div>
                  </>
                )}
                <div className="form-row">
                  <div className="form-group">
                    <label>Vorname</label>
                    <input type="text" className="form-control" value={form.first_name}
                      onChange={(e) => setForm({ ...form, first_name: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label>Nachname</label>
                    <input type="text" className="form-control" value={form.last_name}
                      onChange={(e) => setForm({ ...form, last_name: e.target.value })} required />
                  </div>
                </div>
                <div className="form-group">
                  <label>E-Mail</label>
                  <input type="email" className="form-control" value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Rolle</label>
                    <select className="form-control" value={form.role}
                      onChange={(e) => setForm({ ...form, role: e.target.value })}>
                      <option value="user">Benutzer</option>
                      <option value="admin">Administrator</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Wochenstunden</label>
                    <input type="number" className="form-control" value={form.weekly_hours}
                      onChange={(e) => setForm({ ...form, weekly_hours: parseFloat(e.target.value) })}
                      min="0" max="168" step="0.5" />
                  </div>
                </div>
                {editUser && (
                  <div className="form-group">
                    <label>
                      <input type="checkbox" checked={form.is_active}
                        onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                        style={{ marginRight: 8 }} />
                      Benutzer aktiv
                    </label>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Abbrechen</button>
                <button type="submit" className="btn btn-primary">{editUser ? 'Speichern' : 'Erstellen'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showResetModal && (
        <div className="modal-overlay" onClick={() => setShowResetModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 400 }}>
            <div className="modal-header">
              <h3>Passwort zurücksetzen</h3>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowResetModal(false)}>×</button>
            </div>
            <form onSubmit={handleResetPassword}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Neues Passwort</label>
                  <input type="password" className="form-control" value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)} required minLength={10} />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowResetModal(false)}>Abbrechen</button>
                <button type="submit" className="btn btn-primary">Zurücksetzen</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
